import useNswagClient from '../../../../../hooks/api/useNswagClient';
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../../../../components/shared/useUser';
import { ItemPossibleUOMs, ItemSelected } from '../interfaces/itemValueTypes';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../../../hooks/useLogError';
import { CreateTransferRequest,
  CreateTransferResponse,
  CreateTransferRequest_CreateTransferRequestItem,
  Item,
  Store,
  Uom,
  ValidateItemQuantityRequest,
} from '../../../../../app/services/api/generated';
import { SelectChangeEvent } from '@mui/material';

export const useCreateStockTransfer = (openModal = true) => {
  const { getStores, getFranchiseStores, getTransferItems, getUoms, validateTransferItemQuantity, createTransfer } = useNswagClient();
  const { hasPermissionTo, selectedStore, user } = useContext(UserContext);
  const { logError } = useLogError();
  const [transferType, setTransferType] = useState<string | undefined>('');
  const [note, setNote] = useState('');
  const [itemsToCreate, setItemsToCreate] = useState<ItemSelected[]>([]);
  const [itemPossibleUOMs, setItemPossibleUOMs] = useState<ItemPossibleUOMs[]>([]);
  const [isValidatingItemQuantity, setIsValidatingItemQuantity] = useState<boolean>(false);
  const [isProcessingSubmit, setIsProcessingSubmit] = useState<boolean>(false);
  const [isSubmittedSuccess, setIsSubmittedSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isNoteDisabled, setIsNoteDisabled] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [value, setValue] = useState<string | null>(null);
  const [formSelectedStore, setFormSelectedStore] = useState<Store | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [selectableItems, setSelectableItems] = useState<Item[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState<boolean>(false);
  const [isStoresLoading, setIsStoresLoading] = useState<boolean>(false);
  const hasPermissionToIncomingTransfers = hasPermissionTo(['IncomingTransferWrite']);
  const hasPermissionToOutgoingTransfers = hasPermissionTo(['OutgoingTransferWrite']);
  const dropDownItems = selectableItems.filter(x => !x.isDepositFeeItem).map(i => i.itemNumber + ' - ' + i.description);
  const [insufficientStockWarning, setInsufficientStockWarning] = useState<CreateTransferResponse | null>(null);
  const pendingTransfer = useRef<CreateTransferRequest | null>(null);
  const submitting = useRef(false);
  const isModalOpen = useRef(openModal);
  isModalOpen.current = openModal;
  const isDisableActions = isSubmittedSuccess || isProcessingSubmit || insufficientStockWarning !== null;
  const [ createErrorMessage, setCreateErrorMessage ]= useState('');

  const { t } = useTranslation('stockTransfers');

  const createDate: Date = new Date();

  useEffect(() => {
    if (!openModal) {
      pendingTransfer.current = null;
      setInsufficientStockWarning(null);
    }
  }, [openModal]);

  const cancelInsufficientStockTransfer = () => {
    if (submitting.current) return;
    pendingTransfer.current = null;
    setInsufficientStockWarning(null);
  };

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      if (user?.roles && user.roles.includes('admin')) {
        loadAllStores();
      }
      else if (user?.roles && !user.roles.includes('admin')) {
        loadFranchiseStores();
      }
    }
  }, []);

  const loadAllStores = () => {
    setIsStoresLoading(true);
    getStores().then(
      (res) => {
        if (res?.data?.stores) {
          const sortedStores: Store[] = res.data.stores
            .filter((store) => store.storeNumber !== selectedStore?.storeNumber)
            .sort((a, b) => (a.storeName?.toUpperCase() ?? '').localeCompare(b.storeName?.toUpperCase() ?? ''));

          setAllStores(sortedStores);
        }
      })
      .catch((error) => logError(error))
      .finally(() => setIsStoresLoading(false));
  };


  const loadFranchiseStores = () => {
    if (user?.franchiseName) {
      setIsStoresLoading(true);
      getFranchiseStores(user?.franchiseName).then(
        (res) => {
          if (res?.data?.stores) {
            const sortedStores: Store[] = res.data.stores
              .filter((store) => store.storeNumber !== selectedStore?.storeNumber)
              .sort((a, b) => (a.storeName?.toUpperCase() ?? '').localeCompare(b.storeName?.toUpperCase() ?? ''));

            setAllStores(sortedStores);
          }
        })
        .catch((error) => logError(error))
        .finally(() => setIsStoresLoading(false));
    }
  };


  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    cancelInsufficientStockTransfer();
    setTransferType(event.target.value);
    setIsNoteDisabled(false);
    setItemsToCreate([]);
    setItemPossibleUOMs([]);
  };


  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    if (value != '') {
      setIsItemsLoading(true);

      const from = transferType?.toLowerCase() === 'outgoing' ? selectedStore?.storeNumber : formSelectedStore?.storeNumber;
      const to = transferType?.toLowerCase() === 'outgoing' ? formSelectedStore?.storeNumber : selectedStore?.storeNumber;
      getTransferItems(value, from, to)
        .then(
          (result) => setSelectableItems(result.data?.items ?? []),
        )
        .catch((error) => logError(error))
        .finally(() => setIsItemsLoading(false));
    }
    else {
      setSelectableItems([]);
    }
  };

  const handleItemSelect = async (newValue: string) => {
    setValue('');
    setSearchTerm('');
    const itemToAdd = selectableItems.find(i => i.itemNumber + ' - ' + i.description == newValue);
    if (itemToAdd) {
      const preExistingItem = itemsToCreate.find(x => x.id == itemToAdd.id);
      if (!preExistingItem) {

        const possibleUoms = await possibleUOMs(selectedStore?.storeNumber ?? '', itemToAdd.itemNumber ?? '-1');
        let uomsToSet = itemPossibleUOMs.concat({ id: itemToAdd.id ?? -1, uoms: possibleUoms });

        const selectedItem: ItemSelected = {
          id: itemToAdd.id,
          sku: itemToAdd.sku ?? '',
          uom: possibleUoms[0].code,
          quantity: 0,
          validatedUom: false,
          maxValidQuantity: undefined,
          itemNumber: itemToAdd.itemNumber,
          itemDescription: itemToAdd.description,
          quantityError: false,
          errorDescription: undefined,
          isDepositFeeItem: itemToAdd.isDepositFeeItem ?? false,
          isDepositItem: itemToAdd.isDepositItem ?? false,
          depositFeeItemNumber: itemToAdd.depositFeeItemNumber ?? '',
          isAllowPartialUom: itemToAdd.isAllowPartialUomTransactions,
          newItemNumber : itemToAdd.newItemNumber,
        };

        //find the deposit fee item that goes with this deposit item and add it to the list as well if it is not already there
        const depositFeeItem = selectableItems.find(i => i.sku === selectedItem.depositFeeItemNumber
          && i.storeNumber == (transferType?.toLowerCase() === 'incoming' ? formSelectedStore?.storeNumber : selectedStore?.storeNumber));

        const depositFeeItemToAdd: ItemSelected = {
          id: depositFeeItem?.id,
          sku: depositFeeItem?.sku ?? '',
          uom: possibleUoms[0].code,
          quantity: 0,
          validatedUom: false,
          maxValidQuantity: undefined,
          itemNumber: depositFeeItem?.itemNumber,
          itemDescription: depositFeeItem?.description,
          quantityError: false,
          errorDescription: undefined,
          isDepositFeeItem: depositFeeItem?.isDepositFeeItem ?? false,
          isDepositItem: depositFeeItem?.isDepositItem ?? false,
          depositFeeItemNumber: depositFeeItem?.depositFeeItemNumber ?? '',
          isAllowPartialUom: depositFeeItem?.isAllowPartialUomTransactions,
          newItemNumber : depositFeeItem?.newItemNumber,
        };

        const updatedItems = [selectedItem];

        const depositFeeItemExists = itemsToCreate.find(x => x.id == depositFeeItem?.id);

        if (selectedItem.isDepositItem && !depositFeeItemExists && depositFeeItem) {
          const possibleDepositFeeItemUoms = await possibleUOMs(selectedStore?.storeNumber ?? '', depositFeeItem?.itemNumber ?? '-1');
          uomsToSet = uomsToSet.concat({ id: depositFeeItem?.id ?? -1, uoms: possibleDepositFeeItemUoms });
          updatedItems.push(depositFeeItemToAdd);
        }

        setItemPossibleUOMs(uomsToSet);
        setItemsToCreate(itemsToCreate.concat(updatedItems));
      }
    }
  };

  const handleStoreSelect = async (newValue: Store) => {
    cancelInsufficientStockTransfer();
    setItemsToCreate([]);
    setItemPossibleUOMs([]);
    setFormSelectedStore(newValue);
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const ValidateTransferItemQuantity = async (selectedItem: ItemSelected, id: number) => {
    setIsValidatingItemQuantity(true);
    selectedItem.allowTransfersWithInsufficientStock = false;
    selectedItem.validatedUom = false;

    const transferTypeLower = transferType?.toLowerCase();
    const itemQuantityRequestObj: ValidateItemQuantityRequest = {
      storeNumber: transferTypeLower === 'incoming' ? (formSelectedStore?.storeNumber ?? '') : (selectedStore?.storeNumber ?? ''),
      itemNumber: selectedItem.itemNumber ?? '',
      quantity: Number(selectedItem.quantity),
      uom: selectedItem.uom ?? '',
    };

    await validateTransferItemQuantity(itemQuantityRequestObj)
      .then(
        (result) => {
          selectedItem.validatedUom = true;
          selectedItem.maxValidQuantity = result.maxValidQuantity;
          selectedItem.allowTransfersWithInsufficientStock = result.allowTransfersWithInsufficientStock === true;
          selectedItem.quantityError = !selectedItem.allowTransfersWithInsufficientStock &&
            (selectedItem.quantity <= 0 || !result.isQuantityValid);
          selectedItem.errorDescription = selectedItem.quantityError && selectedItem.quantity > 0
            ? `${t('create_modal.exceeds_available_quantity_of')} ${result.maxValidQuantity}` : '';
        },
      )
      .catch((error) => {
        logError(error);
        selectedItem.allowTransfersWithInsufficientStock = false;
        selectedItem.validatedUom = false;
        selectedItem.maxValidQuantity = 0;
        if (Number(selectedItem.quantity) > selectedItem.maxValidQuantity) {
          selectedItem.quantityError = true;
          selectedItem.errorDescription = `${t('create_modal.exceeds_available_quantity_of')} ${selectedItem.maxValidQuantity}`;
        }
      })
      .finally(() => {
        QtyOrUoMChanges(id, selectedItem.quantity, selectedItem.quantityError, selectedItem.errorDescription ?? '');
        setIsValidatingItemQuantity(false);
      });

  };

  const QtyOrUoMChanges = (id: number, quantity: string | number, quantityError: boolean, errorDescription: string) => {
    const newQuantites = itemsToCreate.map(a => {
      const returnValue = { ...a };
      if (a.id == id) {
        returnValue.quantity = quantity;
        returnValue.quantityError = quantityError;
        returnValue.errorDescription = errorDescription;
      }

      if (a.isDepositFeeItem) {
        const depositItems = itemsToCreate.filter(x => x.isDepositItem && x.depositFeeItemNumber == a.sku);

        if (depositItems.length > 0) {
          let depositQty = depositItems.map(x => {
            return Number(x.quantity);
          }).reduce((accumulator, currentValue) => accumulator + currentValue, 0);

          depositQty = depositQty - Number(itemsToCreate.find(x => x.id == id)?.quantity ?? 0) + Number(quantity);

          const actualDepositQuantity = isNaN(depositQty) ? 0 : Math.max(0, depositQty);
          returnValue.quantity = actualDepositQuantity;
        }
      }

      return returnValue;
    });

    setItemsToCreate(newQuantites);
  };

  const handleQuantityChange = (id: number) => async (event: React.ChangeEvent<HTMLInputElement>) => {
    let requestedQuantity = event.target.value;
    requestedQuantity = Number(requestedQuantity) < 0 ? '0' : requestedQuantity;
    const selectedItem = itemsToCreate.find(x => x.id == id);
    if (selectedItem) {
      selectedItem.quantity = requestedQuantity;
      if (selectedItem.uom && !selectedItem.validatedUom) {
        await ValidateTransferItemQuantity(selectedItem, id);
      }
      else {    
        if(!selectedItem.allowTransfersWithInsufficientStock &&
          (Number(requestedQuantity) <= 0 || (selectedItem.maxValidQuantity ?? 0) <= 0 || Number(requestedQuantity) > (selectedItem.maxValidQuantity ?? 0))) {
          selectedItem.quantityError = true;
          selectedItem.errorDescription = `${t('create_modal.exceeds_available_quantity_of')} ${selectedItem.maxValidQuantity}`;
        }
        else {
          selectedItem.quantityError = false;
          selectedItem.errorDescription = '';
        }
        QtyOrUoMChanges(id, selectedItem.quantity, selectedItem.quantityError, selectedItem.errorDescription ?? '');
      }
    }
  };


  const handleUomChange = (id: number) => async (event: SelectChangeEvent) => {
    const newUoM: string = event.target.value;
    const selectedItem = itemsToCreate.find(x => x.id == id);
    if (selectedItem) {
      selectedItem.uom = newUoM;
      await ValidateTransferItemQuantity(selectedItem, id);
    }

    if (selectedItem?.isDepositItem) {
      const depositFeeItem = itemsToCreate.find(x => x.isDepositFeeItem && x.sku == selectedItem.depositFeeItemNumber);

      if (depositFeeItem) {
        depositFeeItem.uom = newUoM;
        await ValidateTransferItemQuantity(depositFeeItem, depositFeeItem!.id!);
      }
    }
  };

  const possibleUOMs = async (storeNumber: string, itemNumber: string): Promise<Uom[]> => {
    let uomReturn: Uom[] = [];
    await getUoms(itemNumber, storeNumber, true, true)
      .then((results) => {
        const uoms = results.data?.uoms;
        uomReturn = uoms ?? [];
      })
      .catch((error) => logError(error));
    return uomReturn;
  };


  const deleteButtonHandler = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    const itemToRemove = itemsToCreate.find(x => x.id == id);

    //if other deposit items exist for that dpeosit fee item, do not remove the deposit fee item
    const otherDepositItemsExist = itemsToCreate.some(x => x.id != id && x.depositFeeItemNumber == itemToRemove?.depositFeeItemNumber);

    let itemsToSet = itemsToCreate.filter(x => x.id != id);

    if (!otherDepositItemsExist) {
      itemsToSet = itemsToSet.filter(x => x.sku != itemToRemove?.depositFeeItemNumber);
    }
    else {
      const depositFeeItem = itemsToCreate.find(x => x.isDepositFeeItem && x.sku == itemToRemove?.depositFeeItemNumber);
      if (depositFeeItem) {
        const currentQty = Number(depositFeeItem.quantity ?? 0) || 0;
        const removeQty = Number(itemToRemove?.quantity ?? 0) || 0;

        depositFeeItem.quantity = currentQty - removeQty;

        //get uom from the other deposit item that exists for this deposit fee item
        depositFeeItem.uom = itemsToCreate.find(x => x.id != id && x.depositFeeItemNumber == itemToRemove?.depositFeeItemNumber)?.uom ?? '';
        await ValidateTransferItemQuantity(depositFeeItem, depositFeeItem!.id!);
      }
    }

    setItemsToCreate(itemsToSet);
    setItemPossibleUOMs(itemPossibleUOMs.filter(x => x.id != id));
  };

  const errorDismissButtonHandler = () => {
    setIsError(false);
  };

  const successDismissButtonHandler = () => {
    cancelInsufficientStockTransfer();
    setItemsToCreate([]);
    setItemPossibleUOMs([]);
    setFormSelectedStore(null);
    setIsNoteDisabled(true);
    setNote('');

    setIsSubmittedSuccess(false);
  };

  const isFormInvalid = () => {
    if (transferType === '') return true;
    if (itemsToCreate.length == 0) return true;
    if (itemsToCreate.some(item => item.quantityError)) return true;
    if (isValidatingItemQuantity || itemsToCreate.some(item => item.quantity <= 0 || !item.uom || !item.validatedUom)) return true;
    return false;
  };

  const handleSubmit = () => {
    if (submitting.current || insufficientStockWarning || isFormInvalid()) return;
    const items: CreateTransferRequest_CreateTransferRequestItem[] = itemsToCreate.map(item => {
      return {
        itemNumber: item.itemNumber ?? '',
        uom: item.uom ?? '',
        requestedQuantity: Number(item.quantity),
      };
    });

    const body: CreateTransferRequest = {
      transferDate: createDate.toISOString(),
      transferType: transferType ?? '',
      storeNumber: selectedStore?.storeNumber ?? '',
      otherStoreNumber: formSelectedStore?.storeNumber ?? '',
      note: note,
      items: items,
    };

    pendingTransfer.current = body;
    submitTransfer(body);
  };

  const submitTransfer = (body: CreateTransferRequest) => {
    if (submitting.current) return;
    submitting.current = true;
    setIsProcessingSubmit(true);
    setIsError(false);
    createTransfer(body)
      .then((response) => {
        if (response?.insufficientStockItems?.length) {
          if (isModalOpen.current) setInsufficientStockWarning(response);
          return;
        }

        pendingTransfer.current = null;
        setInsufficientStockWarning(null);
        setIsSubmittedSuccess(true);
        setTimeout(() => {
          window.location.reload();
        }, 3000); // Refresh after 3 seconds
      })
      .catch((error) => {
        pendingTransfer.current = null;
        setInsufficientStockWarning(null);
        logError(error);
        let errorMessage = '';
        for (const key in error?.errors) {
          if (Object.prototype.hasOwnProperty.call(error?.errors, key)) {
            const value = error?.errors[key];
            if (Array.isArray(value) && value.length > 0) {
              errorMessage = value[0];  // Return the message part
            }
          }
        }

        setCreateErrorMessage(errorMessage);
        setIsError(true);
      })
      .finally(() => {
        submitting.current = false;
        setIsProcessingSubmit(false);
      });

  };

  const continueInsufficientStockTransfer = () => {
    if (submitting.current || !pendingTransfer.current || !insufficientStockWarning) return;
    submitTransfer({
      ...pendingTransfer.current,
      insufficientStockConfirmation: insufficientStockWarning.confirmationToken,
    });
  };

  return {
    hasPermissionToIncomingTransfers,
    hasPermissionToOutgoingTransfers,
    allStores,
    isFormInvalid,
    handleTypeChange,
    handleSearchTermChange,
    handleItemSelect,
    handleStoreSelect,
    handleNoteChange,
    handleQuantityChange,
    handleUomChange,
    deleteButtonHandler,
    dropDownItems,
    handleSubmit,
    insufficientStockWarning,
    continueInsufficientStockTransfer,
    cancelInsufficientStockTransfer,
    setItemsToCreate,
    transferType,
    createDate,
    value,
    formSelectedStore,
    searchTerm,
    itemsToCreate,
    itemPossibleUOMs,
    isValidatingItemQuantity,
    note,
    isItemsLoading,
    isStoresLoading,
    isProcessingSubmit,
    isSubmittedSuccess,
    isDisableActions,
    isError,
    isNoteDisabled,
    errorDismissButtonHandler,
    successDismissButtonHandler,
    t,
    createErrorMessage,
    setNote,
    setTransferType,
    setFormSelectedStore,
    setValue,
  };
};
